import React, { useState, useEffect } from "react";
import { Card, Button, Row, Col } from "react-bootstrap";
import { FaPlayCircle, FaStar, FaHistory, FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { runbookActions } from "../../actions/runbook.actions";
import { runbookExecutionHistoryActions } from "../../actions/runbookExecutionHistory.actions";
import CollapsibleCard from "../common/CollapsibleCard";
import ConfirmModal from "../common/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";

function FavoriteRunbooks(props) {
  const { hasPermission } = usePermissions(props.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const runbookReducer = useSelector((state) => state.runbook);
  const [favorites, setFavorites] = useState([]);
  const [confirmRunModalShow, setConfirmRunModalShow] = useState(false);
  const [executeRunbook, setExecuteRunbook] = useState({});

  const loadFavorites = () => {
    const saved = localStorage.getItem("favoriteRunbooks");
    if (saved) {
      try {
        setFavorites(JSON.parse(saved));
      } catch (e) {
        setFavorites([]);
      }
    }
  };

  useEffect(() => {
    loadFavorites();

    // Listen for storage changes from other tabs/components
    const handleStorageChange = (e) => {
      if (e.key === "favoriteRunbooks") {
        loadFavorites();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Poll for changes every 10 seconds (reduced from 1 second for performance)
    const interval = setInterval(loadFavorites, 10000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const removeFavorite = (runbookId) => {
    const updated = favorites.filter((f) => f.runbookId !== runbookId);
    setFavorites(updated);
    localStorage.setItem("favoriteRunbooks", JSON.stringify(updated));
  };

  const handleExecuteClick = (runbook) => {
    setExecuteRunbook(runbook);
    setConfirmRunModalShow(true);
  };

  const handleExecuteConfirm = () => {
    setConfirmRunModalShow(false);
    let requestBody = {
      runbookId: executeRunbook.runbookId,
      clientId: executeRunbook.clientId,
      runbookGuid: executeRunbook.runbookGuid,
      clientGuid: executeRunbook.clientGuid,
    };
    dispatch(runbookActions.execute(requestBody));
  };

  // Refresh latest executions after successful execution
  useEffect(() => {
    if (runbookReducer.executeSuccess) {
      let queryParams = {
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      };
      dispatch(runbookExecutionHistoryActions.getLatestExecutions(queryParams));
    }
  }, [runbookReducer.executeSuccess, dispatch, props.user.clientId, props.user.clientGuid]);

  const handleHistory = (runbook) => {
    navigate(`/runbook/executions?runbookId=${runbook.runbookId}`);
  };

  const handleAddFavorite = () => {
    navigate("/runbook");
  };

  return (
    <div data-tour="favorites">
      <CollapsibleCard
        title="Favorite Runbooks"
        icon={<FaStar className="mb-1" />}
        defaultExpanded={true}
      >
      <Row>
        {favorites.length === 0 && (
          <Col>
            <div className="text-center text-muted py-4">
              <p>No favorite runbooks yet</p>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddFavorite}
              >
                <FaPlus className="me-2" />
                Browse Runbooks
              </Button>
            </div>
          </Col>
        )}
        {favorites.map((runbook) => (
          <Col key={runbook.runbookId} md={6} lg={4} className="mb-3">
            <Card className="h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <Card.Title className="mb-0 small">
                    {runbook.runbookName}
                  </Card.Title>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-warning"
                    onClick={() => removeFavorite(runbook.runbookId)}
                    title="Remove from favorites"
                  >
                    <FaStar />
                  </Button>
                </div>
                {runbook.runbookDescription && (
                  <Card.Text className="small text-muted">
                    {runbook.runbookDescription.substring(0, 60)}
                    {runbook.runbookDescription.length > 60 && "..."}
                  </Card.Text>
                )}
                <div className="d-flex gap-2 mt-3">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleExecuteClick(runbook)}
                    disabled={!hasPermission("runbooks.execute") && !props.user.isAdmin && !props.user.isSuperUser}
                    title={
                      !hasPermission("runbooks.execute") && !props.user.isAdmin && !props.user.isSuperUser
                        ? "You don't have permission to execute runbooks"
                        : "Execute this runbook"
                    }
                  >
                    <FaPlayCircle className="me-1" />
                    Execute
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleHistory(runbook)}
                  >
                    <FaHistory className="me-1" />
                    History
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <ConfirmModal
        show={confirmRunModalShow}
        title={`Execute Runbook - ${executeRunbook.runbookId}`}
        question={`Are you sure you want to execute ${executeRunbook.runbookId} - ${executeRunbook.runbookName}?`}
        noButtonText="No"
        yesButtonText="Yes, please"
        handleClose={() => setConfirmRunModalShow(false)}
        handleConfirm={handleExecuteConfirm}
      />
    </CollapsibleCard>
    </div>
  );
}

export default FavoriteRunbooks;